/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React, { useCallback } from 'react'
import { App, Image, Upload } from 'antd'
import type { UploadChangeParam, UploadFile } from 'antd/es/upload'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_SIZE_MB,
} from '@sportninja/common/constants/imageRequirements'
import type { EditPlayerFormValues } from '@sportninja/common/features/profile/player'
import Text from 'src/components/ts-components/Text'

const PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(reader.result as string))
    reader.addEventListener('error', reject)
    reader.readAsDataURL(file)
  })

interface PlayerAvatarProps {
  /** Display only — no upload/remove (roster add-existing keeps the BE image). */
  readOnly?: boolean
}

const PlayerAvatar: React.FC<PlayerAvatarProps> = ({ readOnly = false }) => {
  const { message } = App.useApp()
  const { values, setFieldValue } = useFormikContext<EditPlayerFormValues>()

  // Show the freshly-picked preview if present, else the current image (unless
  // the user removed it).
  const preview = values.imageData
    ? values.imageData
    : values.imageDeleted
    ? ''
    : values.imageUrl

  const beforeUpload = useCallback(
    (file: File): boolean => {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        message.error(
          'Please upload a file in one of the following formats: JPEG, GIF, PNG, AVIF, WebP, TIFF, or HEIC.'
        )
        return false
      }
      if (file.size / 1024 / 1024 > MAX_IMAGE_SIZE_MB) {
        message.error(
          'File is too large. Please upload an image smaller than 5MB.'
        )
        return false
      }
      return false // keep local — do NOT auto-upload
    },
    [message]
  )

  const handleChange = useCallback(
    async (info: UploadChangeParam<UploadFile>) => {
      const file = info.file.originFileObj || (info.file as unknown as File)
      if (!file) {
        return
      }
      // beforeUpload returned false, so re-validate type/size before staging.
      if (
        !ALLOWED_IMAGE_TYPES.includes(file.type) ||
        file.size / 1024 / 1024 > MAX_IMAGE_SIZE_MB
      ) {
        return
      }
      const base64 = await readAsBase64(file)
      setFieldValue('imageData', base64)
      setFieldValue('imageDeleted', false)
    },
    [setFieldValue]
  )

  const handleDelete = useCallback(() => {
    setFieldValue('imageData', '')
    setFieldValue('imageDeleted', true)
  }, [setFieldValue])

  // Single circular slot:
  // - no image  → dashed "+ Upload" control (opens the file picker).
  // - has image → the avatar with a hover overlay exposing Delete. The upload
  //   control is hidden while an image exists; deleting reveals it again.
  return (
    <div css={wrapperStyle}>
      {readOnly ? (
        // Add-existing: show the player's image (or placeholder), no edit.
        <div css={avatarSlotStyle}>
          <Image
            src={preview || PLACEHOLDER}
            alt='player photo'
            width={88}
            height={88}
            preview={false}
            css={avatarImageStyle}
            fallback={PLACEHOLDER}
          />
        </div>
      ) : preview ? (
        <div css={avatarSlotStyle}>
          <Image
            src={preview}
            alt='player photo'
            width={88}
            height={88}
            preview={false}
            css={avatarImageStyle}
            fallback={PLACEHOLDER}
          />
          <div css={hoverOverlayStyle}>
            <button
              type='button'
              css={overlayActionStyle}
              onClick={handleDelete}
              aria-label='Remove player photo'
            >
              <DeleteOutlined css={deleteIconStyle} />
            </button>
          </div>
        </div>
      ) : (
        <Upload
          beforeUpload={beforeUpload}
          onChange={handleChange}
          showUploadList={false}
          accept={ALLOWED_IMAGE_TYPES.join(',')}
        >
          <button
            type='button'
            css={uploadButtonStyle}
            aria-label='Upload player photo'
          >
            <PlusOutlined css={uploadIconStyle} />
            <Text
              variant='captionXSmall'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              Upload
            </Text>
          </button>
        </Upload>
      )}
    </div>
  )
}

export default PlayerAvatar

// ---- styled ----
const wrapperStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const uploadButtonStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  border: 1px dashed ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_300};
  color: ${colors.NEUTRAL_0};
  cursor: pointer;

  &:hover {
    border-color: ${colors.PRIMARY};
  }
`

const uploadIconStyle = css`
  font-size: 18px;
  color: ${colors.NEUTRAL_0};
`

// Circular slot that clips the image and reveals the action overlay on hover.
// On hover/focus the image blurs and the dark overlay + actions fade in.
const avatarSlotStyle = css`
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  overflow: hidden;

  &:hover > div,
  &:focus-within > div {
    opacity: 1;
  }

  &:hover .ant-image-img,
  &:focus-within .ant-image-img {
    filter: blur(3px);
  }
`

const avatarImageStyle = css`
  border-radius: 50%;
  object-fit: cover;
  transition: filter 0.15s ease;
`

// Dark overlay holding the delete (and future preview) actions; fades in on hover.
const hoverOverlayStyle = css`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  opacity: 0;
  transition: opacity 0.15s ease;
`

const overlayActionStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`

const deleteIconStyle = css`
  font-size: 22px;
  color: ${colors.ERROR_200};
`
